import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api, apiClient, getApiBaseUrl, type IBankAccount } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useBranding, ColorScheme, type SocialLinksData } from '../../context/BrandingContext';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import FormError from '../../components/ui/FormError';
import PageHeader from '../../components/PageHeader';
import MarkdownEditor from '../../components/MarkdownEditor';
import ToggleSwitch from '../../components/forms/ToggleSwitch';
import '../../App.css';
import './OrganizationSettingsPage.css';
import { translateError } from '../../utils/errorMessages';

// Helper to get full URL for uploaded assets
const getAssetUrl = (path: string | undefined): string => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/uploads')) return `${getApiBaseUrl()}${path}`;
  return path;
};



interface IBranding {
  logo?: string;
  favicon?: string;
  colorScheme: ColorScheme;
  defaultTheme?: 'light' | 'dark';
}

interface IWelcomePage {
  title?: string;
  description?: string;
  bannerImage?: string | null;
  backgroundImage?: string | null;
  backgroundOpacity?: number;
  showEventMarquee?: boolean;
}

interface IOrganization {
  name: string;
  contactEmail: string;
  bankAccounts: IBankAccount[];
  branding?: IBranding;
  welcomePage?: IWelcomePage;
  settings?: { socialLinks?: SocialLinksData };
}

const SOCIAL_NETWORKS = ['instagram', 'facebook', 'website'] as const;

const COLOR_SCHEMES: { value: ColorScheme; label: string; color: string }[] = [
  { value: 'default', label: 'Default (Orange)', color: '#ff8c00' },
  { value: 'blue', label: 'Blue', color: '#3182ce' },
  { value: 'green', label: 'Green', color: '#38a169' },
  { value: 'purple', label: 'Purple', color: '#805ad5' },
  { value: 'orange', label: 'Orange', color: '#ff8c00' },
  { value: 'red', label: 'Red', color: '#e53e3e' },
];

const OrganizationSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { refreshBranding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [orgName, setOrgName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  // Bank accounts save independently of the rest of this page (migration 0044
  // gave them their own table and per-row endpoints), so they carry their own
  // draft buffers and status messages.
  const [bankAccounts, setBankAccounts] = useState<IBankAccount[]>([]);
  const [accountDrafts, setAccountDrafts] = useState<Record<number, { name: string; accountNumber: string }>>({});
  const [newAccount, setNewAccount] = useState({ name: '', accountNumber: '' });
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountMessage, setAccountMessage] = useState<string | null>(null);

  // Branding state
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [colorScheme, setColorScheme] = useState<ColorScheme>('default');
  const [defaultTheme, setDefaultTheme] = useState<'light' | 'dark'>('dark');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);

  // Social links state
  const [socialLinks, setSocialLinks] = useState<SocialLinksData>({});

  // Required member fields state
  const [requiredMemberFields, setRequiredMemberFields] = useState<Record<string, boolean>>({});

  // Welcome page state
  const [wpTitle, setWpTitle] = useState('');
  const [wpDescription, setWpDescription] = useState('');
  const [wpBannerUrl, setWpBannerUrl] = useState('');
  const [wpShowMarquee, setWpShowMarquee] = useState(true);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [wpBannerPreview, setWpBannerPreview] = useState<string | null>(null);
  const [wpBackgroundUrl, setWpBackgroundUrl] = useState('');
  const [wpBackgroundOpacity, setWpBackgroundOpacity] = useState(0.5);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [wpBackgroundPreview, setWpBackgroundPreview] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);

  // Permission check
  const canEdit = hasPermission('organization:write');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.organization.get();
        const orgData = data as IOrganization;

        setOrgName(orgData.name || '');
        setContactEmail(orgData.contactEmail || '');
        applyAccounts(orgData.bankAccounts || []);

        // Branding
        if (orgData.branding) {
          setLogoUrl(orgData.branding.logo || '');
          setFaviconUrl(orgData.branding.favicon || '');
          setColorScheme(orgData.branding.colorScheme || 'default');
          setDefaultTheme(orgData.branding.defaultTheme || 'dark');
        }

        // Required member fields
        if ((data as any).settings?.requiredMemberFields) {
          setRequiredMemberFields((data as any).settings.requiredMemberFields);
        }

        // Social links
        setSocialLinks(orgData.settings?.socialLinks || {});

        // Welcome page
        if (orgData.welcomePage) {
          setWpTitle(orgData.welcomePage.title || '');
          setWpDescription(orgData.welcomePage.description || '');
          setWpBannerUrl(orgData.welcomePage.bannerImage || '');
          setWpBackgroundUrl(orgData.welcomePage.backgroundImage || '');
          setWpBackgroundOpacity(
            typeof orgData.welcomePage.backgroundOpacity === 'number'
              ? orgData.welcomePage.backgroundOpacity
              : 0.5,
          );
          setWpShowMarquee(orgData.welcomePage.showEventMarquee !== false);
        }

      } catch (err: any) {
        setError(translateError(err, t));
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Replace the list and reset every draft to match it. Each mutation endpoint
  // returns the full refreshed list, so this is the single write path.
  const applyAccounts = (accounts: IBankAccount[]) => {
    setBankAccounts(accounts);
    // Preserve unsaved edits on OTHER rows: acting on account B (promote,
    // delete, add) must not silently discard what the admin has typed into
    // account A. A draft that already matches the server is simply reseeded,
    // which is what happens to the row that was just saved.
    setAccountDrafts((prev) =>
      Object.fromEntries(
        accounts.map((a) => {
          const server = { name: a.name ?? '', accountNumber: a.accountNumber };
          const draft = prev[a._id];
          const edited = draft
            && (draft.name !== server.name || draft.accountNumber !== server.accountNumber);
          return [a._id, edited ? draft : server];
        }),
      ),
    );
  };

  /** Returns whether the action succeeded, so callers can avoid discarding
   *  the admin's input on failure (a rejected account number must stay in the
   *  field to be corrected, not vanish). */
  const runAccountAction = async (
    action: () => Promise<{ data: IBankAccount[] }>,
    successKey: string,
  ): Promise<boolean> => {
    setAccountBusy(true);
    setAccountError(null);
    setAccountMessage(null);
    try {
      const { data } = await action();
      applyAccounts(data);
      setAccountMessage(t(successKey));
      return true;
    } catch (err: any) {
      setAccountError(translateError(err, t));
      return false;
    } finally {
      setAccountBusy(false);
    }
  };

  const handleAccountDraftChange = (id: number, field: 'name' | 'accountNumber', value: string) => {
    setAccountDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { name: '', accountNumber: '' }), [field]: value },
    }));
  };

  const handleSaveAccount = (account: IBankAccount) => {
    const draft = accountDrafts[account._id];
    if (!draft) return;
    return runAccountAction(
      () => api.organization.bankAccounts.update(account._id, {
        name: draft.name.trim() || null,
        accountNumber: draft.accountNumber.trim(),
      }),
      'organization.accountSaved',
    );
  };

  // Promotion only — the default is changed by naming a different account, so
  // the organization is never left without one.
  const handleSetDefaultAccount = (account: IBankAccount) =>
    runAccountAction(
      // Deliberately the saved values, not the row's draft: promoting an
      // account must not also commit unrelated half-typed edits.
      () => api.organization.bankAccounts.update(account._id, {
        name: account.name,
        accountNumber: account.accountNumber,
        isDefault: true,
      }),
      'organization.accountDefaultSet',
    );

  const handleAddAccount = async () => {
    if (!newAccount.accountNumber.trim()) return;
    const ok = await runAccountAction(
      () => api.organization.bankAccounts.create({
        name: newAccount.name.trim() || null,
        accountNumber: newAccount.accountNumber.trim(),
      }),
      'organization.accountAdded',
    );
    // Only clear on success — the account-number validator is strict (Czech
    // mod-11 checksum), so a rejected value must stay put to be fixed.
    if (ok) setNewAccount({ name: '', accountNumber: '' });
  };

  // The backend answers 409 when an activity or an imported transaction still
  // references the account; its message names what is in the way.
  const handleRemoveAccount = (account: IBankAccount) =>
    runAccountAction(
      () => api.organization.bankAccounts.delete(account._id),
      'organization.accountRemoved',
    );

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      // The unified uploads router accepts every kind under the field 'file'.
      formData.append('file', file);
      const response = await apiClient.post<{ url: string }>('/api/uploads/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(response.data.url);
      setLogoPreview(null);
      setMessage(t('organization.branding.logoUploaded'));
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setFaviconPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      // The unified uploads router accepts every kind under the field 'file'.
      formData.append('file', file);
      const response = await apiClient.post<{ url: string }>('/api/uploads/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFaviconUrl(response.data.url);
      setFaviconPreview(null);
      setMessage(t('organization.branding.faviconUploaded'));
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
      setFaviconPreview(null);
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleColorSchemeChange = async (scheme: ColorScheme) => {
    setColorScheme(scheme);
    try {
      await api.organization.update({
        branding: {
          logo: logoUrl,
          favicon: faviconUrl,
          colorScheme: scheme
        }
      });
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setWpBannerPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploadingBanner(true);
    try {
      const response = await api.uploads.welcomeBanner(file);
      setWpBannerUrl(response.data.url);
      setWpBannerPreview(null);
      setMessage(t('organization.welcomePage.bannerUploaded'));
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
      setWpBannerPreview(null);
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleDeleteBanner = async () => {
    try {
      await api.uploads.deleteWelcomeBanner();
      setWpBannerUrl('');
      setMessage(t('organization.welcomePage.bannerDeleted'));
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
    }
  };

  const handleBackgroundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setWpBackgroundPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploadingBackground(true);
    try {
      const response = await api.uploads.welcomeBackground(file);
      setWpBackgroundUrl(response.data.url);
      setWpBackgroundPreview(null);
      setMessage(t('organization.welcomePage.backgroundUploaded'));
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
      setWpBackgroundPreview(null);
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleDeleteBackground = async () => {
    try {
      await api.uploads.deleteWelcomeBackground();
      setWpBackgroundUrl('');
      setMessage(t('organization.welcomePage.backgroundDeleted'));
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      await api.organization.update({
        name: orgName,
        contactEmail,
        settings: {
          requiredMemberFields,
          socialLinks: {
            instagram: socialLinks.instagram ?? '',
            facebook: socialLinks.facebook ?? '',
            website: socialLinks.website ?? '',
          },
        },
        branding: {
          logo: logoUrl,
          favicon: faviconUrl,
          colorScheme,
          defaultTheme,
        },
        welcomePage: {
          title: wpTitle,
          description: wpDescription,
          bannerImage: wpBannerUrl || null,
          backgroundImage: wpBackgroundUrl || null,
          backgroundOpacity: wpBackgroundOpacity,
          showEventMarquee: wpShowMarquee,
        },
      });
      setMessage(t('organization.updateSuccess'));
      refreshBranding();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>{t('common.loading')}</p>;

  if (!canEdit) {
    return (
      <div className="admin-form-page" style={{ maxWidth: '800px' }}>
        <PageHeader title={t('organization.title')} helpKey="help.admin.organization" />
        <p className="error-message">{t('common.noPermission')}</p>
      </div>
    );
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-dark)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '1.5rem',
  };

  const sectionTitle: React.CSSProperties = {
    marginTop: 0,
    marginBottom: '0.25rem',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '0.5rem',
  };

  const sectionDesc: React.CSSProperties = {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
    marginBottom: '1rem',
  };

  return (
    <div className="admin-form-page" style={{ maxWidth: '1200px' }}>
      <PageHeader title={t('organization.title')} helpKey="help.admin.organization" />

      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      <form onSubmit={handleSubmit}>
        <div className="org-settings__grid">

          {/* ============ LEFT COLUMN: Settings ============ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* General Settings Card */}
            <div style={cardStyle}>
              <h3 style={sectionTitle}>{t('organization.title')}</h3>

              <FormField htmlFor="org-name" label={t('organization.name')} required>
                <input
                  type="text"
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                />
              </FormField>

              <FormField htmlFor="org-contact-email" label={t('organization.contactEmail')}>
                <input
                  type="email"
                  id="org-contact-email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </FormField>

              {/* Color Scheme */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{t('organization.branding.colorScheme')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  {COLOR_SCHEMES.map((scheme) => (
                    <button
                      key={scheme.value}
                      type="button"
                      onClick={() => handleColorSchemeChange(scheme.value)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.4rem 0.75rem',
                        border: colorScheme === scheme.value ? `2px solid ${scheme.color}` : '2px solid var(--border-color)',
                        borderRadius: '6px',
                        backgroundColor: colorScheme === scheme.value ? 'var(--bg-light)' : 'var(--bg-medium)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '0.85rem',
                      }}
                    >
                      <span style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: scheme.color, border: '2px solid rgba(255,255,255,0.2)', flexShrink: 0 }} />
                      <span>{t(`organization.branding.schemes.${scheme.value}`)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Default Theme (Light/Dark) for visitors */}
              <div className="form-group" style={{ marginTop: '1rem', marginBottom: 0 }}>
                <label>{t('organization.branding.defaultTheme')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {(['dark', 'light'] as const).map((t2) => (
                    <button
                      key={t2}
                      type="button"
                      onClick={() => setDefaultTheme(t2)}
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        border: defaultTheme === t2 ? '2px solid var(--orange-primary)' : '2px solid var(--border-color)',
                        borderRadius: '6px',
                        backgroundColor: defaultTheme === t2 ? 'var(--bg-light)' : 'var(--bg-medium)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '0.9rem',
                        fontWeight: defaultTheme === t2 ? 600 : 400,
                      }}
                    >
                      {t(`organization.branding.theme${t2 === 'dark' ? 'Dark' : 'Light'}`)}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  {t('organization.branding.defaultThemeHint')}
                </p>
              </div>
            </div>

            {/* Bank Accounts Card */}
            <div style={cardStyle}>
              <h3 style={sectionTitle}>{t('organization.bankAccounts')}</h3>
              <p style={sectionDesc}>{t('organization.bankAccountsDesc')}</p>

              {accountError && <FormError message={accountError} />}
              {accountMessage && (
                <p style={{ color: 'var(--success, #28a745)', marginBottom: '1rem' }}>{accountMessage}</p>
              )}

              {bankAccounts.map((account) => {
                const draft = accountDrafts[account._id] ?? { name: '', accountNumber: '' };
                const dirty =
                  draft.name !== (account.name ?? '') || draft.accountNumber !== account.accountNumber;

                return (
                  <div
                    key={account._id}
                    style={{
                      background: 'var(--bg-darkest)',
                      padding: '1rem',
                      borderRadius: '0.5rem',
                      marginBottom: '1rem',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, color: 'var(--orange-primary)', fontSize: '0.9rem' }}>
                        {account.name || account.accountNumber}
                        {account.isDefault && ` · ${t('organization.defaultAccountBadge')}`}
                      </h4>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        disabled={accountBusy}
                        onClick={() => handleRemoveAccount(account)}
                      >
                        {t('organization.removeAccount')}
                      </Button>
                    </div>

                    <FormField
                      htmlFor={`bank-account-name-${account._id}`}
                      label={t('organization.accountLabel')}
                    >
                      <input
                        type="text"
                        id={`bank-account-name-${account._id}`}
                        value={draft.name}
                        onChange={(e) => handleAccountDraftChange(account._id, 'name', e.target.value)}
                        placeholder={t('organization.accountPlaceholder')}
                      />
                    </FormField>

                    <FormField
                      htmlFor={`bank-account-number-${account._id}`}
                      label={t('organization.accountNumber')}
                      helperText={t('organization.accountNumberHint')}
                    >
                      <input
                        type="text"
                        id={`bank-account-number-${account._id}`}
                        value={draft.accountNumber}
                        onChange={(e) => handleAccountDraftChange(account._id, 'accountNumber', e.target.value)}
                        placeholder="19-2000145399/0800"
                      />
                    </FormField>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Button
                        type="button"
                        size="sm"
                        disabled={accountBusy || !dirty || !draft.accountNumber.trim()}
                        onClick={() => handleSaveAccount(account)}
                      >
                        {t('common.save')}
                      </Button>
                      {!account.isDefault && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={accountBusy}
                          onClick={() => handleSetDefaultAccount(account)}
                        >
                          {t('organization.makePrimary')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              <div
                style={{
                  background: 'var(--bg-darkest)',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: '1px dashed var(--border-color)',
                }}
              >
                <h4 style={{ margin: '0 0 0.75rem', color: 'var(--orange-primary)', fontSize: '0.9rem' }}>
                  {t('organization.addAccount')}
                </h4>

                <FormField htmlFor="bank-account-name-new" label={t('organization.accountLabel')}>
                  <input
                    type="text"
                    id="bank-account-name-new"
                    value={newAccount.name}
                    onChange={(e) => setNewAccount((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={t('organization.accountPlaceholder')}
                  />
                </FormField>

                <FormField
                  htmlFor="bank-account-number-new"
                  label={t('organization.accountNumber')}
                  helperText={t('organization.accountNumberHint')}
                >
                  <input
                    type="text"
                    id="bank-account-number-new"
                    value={newAccount.accountNumber}
                    onChange={(e) => setNewAccount((prev) => ({ ...prev, accountNumber: e.target.value }))}
                    placeholder="19-2000145399/0800"
                  />
                </FormField>

                <Button
                  type="button"
                  disabled={accountBusy || !newAccount.accountNumber.trim()}
                  onClick={handleAddAccount}
                >
                  + {t('organization.addAccount')}
                </Button>
              </div>
            </div>
          </div>

          {/* ============ RIGHT COLUMN: Branding ============ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Required Member Fields Card */}
            <div style={cardStyle}>
              <h3 style={sectionTitle}>{t('settings.requiredMemberFields')}</h3>
              <p style={sectionDesc}>{t('settings.requiredMemberFieldsDescription')}</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {(['dateOfBirth', 'birthNumber', 'email', 'phone', 'address', 'nationality', 'allergies'] as const).map((field) => (
                  <ToggleSwitch
                    key={field}
                    checked={!!requiredMemberFields[field]}
                    onChange={(checked) => {
                      setRequiredMemberFields(prev => ({
                        ...prev,
                        [field]: checked,
                      }));
                    }}
                    label={t(`settings.field.${field}`)}
                  />
                ))}
              </div>
            </div>

            {/* Logo & Favicon Card */}
            <div style={cardStyle}>
              <h3 style={sectionTitle}>{t('organization.branding.title')}</h3>
              <p style={sectionDesc}>{t('organization.branding.description')}</p>

              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {/* Logo */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>{t('organization.branding.logo')}</label>
                  <div
                    style={{ width: '200px', height: '80px', border: '2px dashed var(--border-color)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-medium)', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {(logoPreview || logoUrl) ? (
                      <img src={logoPreview || getAssetUrl(logoUrl)} alt="Logo preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t('organization.branding.clickToUpload')}</span>
                    )}
                    {uploadingLogo && (
                      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>{t('common.loading')}</div>
                    )}
                  </div>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>{t('organization.branding.logoHint')}</p>
                </div>

                {/* Favicon */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>{t('organization.branding.favicon')}</label>
                  <div
                    style={{ width: '80px', height: '80px', border: '2px dashed var(--border-color)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-medium)', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                    onClick={() => faviconInputRef.current?.click()}
                  >
                    {(faviconPreview || faviconUrl) ? (
                      <img src={faviconPreview || getAssetUrl(faviconUrl)} alt="Favicon preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center' }}>{t('organization.branding.clickToUpload')}</span>
                    )}
                    {uploadingFavicon && (
                      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem' }}>{t('common.loading')}</div>
                    )}
                  </div>
                  <input ref={faviconInputRef} type="file" accept="image/*" onChange={handleFaviconUpload} style={{ display: 'none' }} />
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>{t('organization.branding.faviconHint')}</p>
                </div>
              </div>
            </div>

            {/* Social Links Card */}
            <div style={cardStyle}>
              <h3 style={sectionTitle}>{t('organization.socialLinks.title')}</h3>
              <p style={sectionDesc}>{t('organization.socialLinks.description')}</p>

              {SOCIAL_NETWORKS.map((network) => (
                <FormField
                  key={network}
                  htmlFor={`social-${network}`}
                  label={t(`organization.socialLinks.${network}`)}
                >
                  <input
                    type="url"
                    id={`social-${network}`}
                    value={socialLinks[network] ?? ''}
                    onChange={(e) => setSocialLinks((prev) => ({ ...prev, [network]: e.target.value }))}
                    placeholder={t(`organization.socialLinks.${network}Placeholder`)}
                    maxLength={500}
                    disabled={!canEdit}
                  />
                </FormField>
              ))}

              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {t('organization.socialLinks.hint')}
              </p>
            </div>

            {/* Welcome Page Card */}
            <div style={cardStyle}>
              <h3 style={sectionTitle}>{t('organization.welcomePage.title')}</h3>
              <p style={sectionDesc}>{t('organization.welcomePage.description')}</p>

              <FormField htmlFor="welcome-title" label={t('organization.welcomePage.pageTitle')}>
                <input type="text" id="welcome-title" value={wpTitle} onChange={(e) => setWpTitle(e.target.value)} placeholder={t('organization.welcomePage.pageTitlePlaceholder')} maxLength={200} />
              </FormField>

              <FormField htmlFor="welcome-description" label={t('organization.welcomePage.pageDescription')}>
                <MarkdownEditor id="welcome-description" value={wpDescription} onChange={setWpDescription} placeholder={t('organization.welcomePage.pageDescriptionPlaceholder')} rows={6} />
              </FormField>

              {/* Banner Upload */}
              <div className="form-group">
                <label>{t('organization.welcomePage.bannerImage')}</label>
                <div
                  style={{ width: '100%', height: '140px', border: '2px dashed var(--border-color)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-medium)', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                  onClick={() => bannerInputRef.current?.click()}
                >
                  {(wpBannerPreview || wpBannerUrl) ? (
                    <img src={wpBannerPreview || getAssetUrl(wpBannerUrl)} alt="Banner preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t('organization.branding.clickToUpload')}</span>
                  )}
                  {uploadingBanner && (
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>{t('common.loading')}</div>
                  )}
                </div>
                <input ref={bannerInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleBannerUpload} style={{ display: 'none' }} />
                {wpBannerUrl && (
                  <Button type="button" variant="danger" size="sm" onClick={handleDeleteBanner} style={{ marginTop: '0.5rem' }}>
                    {t('organization.welcomePage.removeBanner')}
                  </Button>
                )}
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>{t('organization.welcomePage.bannerHint')}</p>
              </div>

              {/* Background Image Upload */}
              <div className="form-group">
                <label>{t('organization.welcomePage.backgroundImage')}</label>
                <div
                  style={{ width: '100%', height: '140px', border: '2px dashed var(--border-color)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-medium)', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                  onClick={() => backgroundInputRef.current?.click()}
                >
                  {(wpBackgroundPreview || wpBackgroundUrl) ? (
                    <img src={wpBackgroundPreview || getAssetUrl(wpBackgroundUrl)} alt="Background preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t('organization.branding.clickToUpload')}</span>
                  )}
                  {uploadingBackground && (
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>{t('common.loading')}</div>
                  )}
                </div>
                <input ref={backgroundInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleBackgroundUpload} style={{ display: 'none' }} />
                {wpBackgroundUrl && (
                  <Button type="button" variant="danger" size="sm" onClick={handleDeleteBackground} style={{ marginTop: '0.5rem' }}>
                    {t('organization.welcomePage.removeBackground')}
                  </Button>
                )}
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>{t('organization.welcomePage.backgroundHint')}</p>
              </div>

              {/* Background Opacity Slider */}
              {wpBackgroundUrl && (
                <div className="form-group">
                  <label>{t('organization.welcomePage.backgroundOpacity')}: {Math.round(wpBackgroundOpacity * 100)}%</label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={Math.round(wpBackgroundOpacity * 100)}
                    onChange={(e) => setWpBackgroundOpacity(parseInt(e.target.value, 10) / 100)}
                    style={{ width: '100%' }}
                  />
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>{t('organization.welcomePage.backgroundOpacityHint')}</p>
                </div>
              )}

              {/* Marquee Toggle */}
              <div className="form-group">
                <ToggleSwitch
                  checked={wpShowMarquee}
                  onChange={setWpShowMarquee}
                  label={<span>{t('organization.welcomePage.showEventMarquee')}</span>}
                />
              </div>

              {/* Preview */}
              <Button type="button" onClick={() => window.open('/?preview=true', '_blank')} style={{ fontSize: '0.9rem' }}>
                {t('organization.welcomePage.preview')}
              </Button>
            </div>
          </div>
        </div>

        {/* Save Button - full width below both columns */}
        <div style={{ marginTop: '1.5rem' }}>
          <Button type="submit" fullWidth loading={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationSettingsPage;
